// Full-document render of the Oscar basket view: PageSkeleton (the React
// document shell) wrapping the `#basket-app` SPA mount point and the
// CheckoutSettings-driven `#aux-basket-content` region.
import { captureException } from "@sentry/browser";
import React from "react";

import { interfaces, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { BasketApp } from "../apps/checkout/BasketApp";
import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";

// Render-nothing error boundary scoped to the auxiliary region. Without it a
// throw in the payload parse/dispatch below would bubble to PageSkeleton's
// document-level ErrorBoundary and blank the ENTIRE cart body. Rendering
// nothing (rather than the shared ErrorBoundary's message div) mirrors the
// server seam's ""-degrade (safe() around the aux_basket_content hook in
// render_basket): the cart keeps working and only the aux region disappears.
class AuxContentBoundary extends React.PureComponent<
    { children: React.ReactNode },
    { failed: boolean }
> {
    public state = { failed: false };

    componentDidCatch(error: Error): void {
        captureException(error);
        this.setState({ failed: true });
    }

    render(): React.ReactNode {
        return this.state.failed ? null : this.props.children;
    }
}

const AuxBasketContentInner = (props: {
    payload: string;
}): React.JSX.Element | null => {
    if (!props.payload) {
        return null;
    }
    // The payload is TempurpedicDocumentHooks.aux_basket_content's
    // reactivated-serialized BasketAuxContentInterface (the typed boundary
    // for the opaque JSON string the shared BasketDocumentTemplate carries):
    // the CheckoutSettings.aux_basket_content Toolbox stream plus the 7
    // get_toolbox_block_ssr side-channels the page/request/settings-dependent
    // blocks read. Rendering it through the toolbox page map (instead of
    // injecting server HTML) keeps the blocks real React children of the
    // hydrated document, so the interactive ones still work.
    const aux = JSON.parse(
        props.payload,
    ) as interfaces.BasketAuxContentInterface;
    const stream = aux.checkout_settings.aux_basket_content;
    if (!stream) {
        return null;
    }
    const map = buildToolboxPageMap(aux);
    // Wagtail's StreamBlock.render_basic wraps every top-level block of
    // `checkout_settings.aux_basket_content` in
    // `<div class="w-block-{type} block-{type}">`; reproduced per top-level
    // block via the per-block `StreamField tagName="div"` render (BasicPage
    // body precedent).
    return (
        <>
            {stream.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={map}
                />
            ))}
        </>
    );
};

// Exported for direct unit coverage of the containment behavior.
export const AuxBasketContent = (props: {
    payload: string;
}): React.JSX.Element => (
    <AuxContentBoundary>
        <AuxBasketContentInner payload={props.payload} />
    </AuxContentBoundary>
);

export const Template = (
    props: templates.BasketDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* React 19 hoists this into <head>. The
                description meta renders from page_chrome.search_description. */}
            <meta name="chatTags" content="basket" />
            {/* The basket app lives in the document's React tree (mounted
                client-side by BasketApp; the dependency tree is not SSR-safe).
                The #basket-app id is retained for the 60-minute redirect timer
                in apps/checkout/index.tsx. */}
            <div id="basket-app" className="basket-container">
                <BasketApp />
            </div>
            <div id="aux-basket-content">
                <AuxBasketContent payload={props.aux_basket_content} />
            </div>
        </PageSkeleton>
    );
};
